import React, { useEffect, useRef, useState } from 'react';
import rowGrouper from 'lodash/groupBy';
import DataGrid, {
  SelectColumn,
  Column,
  TextEditor,
  RowRendererProps,
  Row,
  DataGridHandle,
  RowHeightArgs,
} from 'react-data-grid';
import { Chip, IconButton, Stack } from '@mui/material';
import { Icon } from '@iconify/react';
import plusOutlinedIcon from '@iconify/icons-ant-design/plus-outlined';
import copyOutlinedIcon from '@iconify/icons-ant-design/copy-outlined';
import deleteOutlinedIcon from '@iconify/icons-ant-design/delete-outlined';
import RoutineOrderComponent from 'components/RoutineOrderComponent';
import PartAutoCompleteComponent from 'components/PartAutoCompleteComponent';
import EmptyCell from 'components/EmptyCell';
import AltWorkoutsPartComponent from 'components/AltWorkoutsPartComponent';
import WorkoutFormatterComponent from 'components/WorkoutFormatterComponent';
import WorkoutIdsAutoCompleteComponent from 'components/WorkoutIdsAutoCompleteComponent';
import RoutineTipFormatter from 'components/RoutineTipFormatter';
import RoutineTipComponent from 'components/RoutineTipComponent';
import WorkoutsAutoComplteComponent from './WorkoutsAutoComplteComponent';
import OneRmAutoCompleteComponent from './OneRmAutoCompleteComponent';
import ChallengeDuplicateModal from '../ChallengeDuplicateModal';
import {
  ChipWrapper,
  CopyButton,
  DuplicateButton,
  HeaderButtonWrapper,
  RestButton,
} from './style';

interface IProps {
  routine: IChallengeRoutines[];
  defaultExpendedGroup: string[];
  newNextRowRoutine: (row: IChallengeRoutines) => void;
  copyNextRowRoutine: (row: IChallengeRoutines) => void;
  deleteRowRoutine: (row: IChallengeRoutines) => void;
  updateValue: (row: IChallengeRoutines, idx: number) => void;
  makeDuplicateRoutines: (
    duplicate: { day: number; checked: boolean; selectedDay: boolean }[],
    selectedDay: number,
    currentRoutines: IChallengeRoutines[],
  ) => void;
  onChangeRestDay: (day: number) => void;
  totalDate: number;
  nextOrderRoutine: (row: IChallengeRoutines) => void;
  prevOrderRoutine: (row: IChallengeRoutines) => void;
}

function rowKeyGetter(row: IChallengeRoutines) {
  return row.id as string | number;
}

function RowRenderer(props: RowRendererProps<IChallengeRoutines>) {
  return <Row {...props} />;
}

function RestFormmater({ row, columnName }: any) {
  if (row.name === 'isRest') {
    return <EmptyCell />;
  }

  return <span>{row[columnName] === 0 ? '' : row[columnName]}</span>;
}
function RirFormmater({ row, columnName }: any) {
  if (row.name === 'isRest') {
    return <EmptyCell />;
  }

  return <span>{row[columnName] === '' ? null : row[columnName]}</span>;
}

export function ChallengeTable({
  routine,
  defaultExpendedGroup,
  newNextRowRoutine,
  copyNextRowRoutine,
  deleteRowRoutine,
  updateValue,
  onChangeRestDay,
  makeDuplicateRoutines,
  totalDate,
  nextOrderRoutine,
  prevOrderRoutine,
}: IProps) {
  const dataGridRef = useRef<any>();
  const [changeWorkoutClipboard, setChangeWorkoutClipboard] = useState<
    Workout[] | null | undefined
  >([]);
  const [duplicateModal, setDuplicateModal] = useState({
    isOpen: false,
    selectedDay: 0,
  });
  const [expandedGroupIds, setExpandedGroupIds] = useState<
    ReadonlySet<unknown>
  >(() => new Set<unknown>(defaultExpendedGroup));
  const columns: Column<IChallengeRoutines>[] = [
    {
      key: 'date',
      name: '일차',
      formatter() {
        return <span />;
      },
      width: 60,
    },
    {
      key: 'name',
      name: '이름',
      width: 200,
      editor: WorkoutsAutoComplteComponent,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row }) {
        return <WorkoutFormatterComponent row={row} />;
      },
    },
    {
      key: 'part',
      name: '운동부위',
      width: 80,
      formatter({ row }) {
        if (
          row.name === 'isRest'
          //  || row.alt_workouts_part
        ) {
          return <EmptyCell />;
        }

        return <span>{row.part}</span>;
      },
    },
    {
      key: 'workout_ids',
      name: '대체운동',
      width: 200,
      editor: WorkoutIdsAutoCompleteComponent,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row, onRowChange }) {
        if (
          row.name === 'isRest'
          //  || row.alt_workouts_part
        ) {
          return <EmptyCell />;
        }

        return (
          <div>
            <ChipWrapper>
              {row.workout_ids_array &&
                row.workout_ids_array.length > 0 &&
                row.workout_ids_array.map((item) => (
                  <Chip size='small' label={item.name} key={`${item.id}`} />
                ))}
            </ChipWrapper>
            <CopyButton
              onClick={(event: any) => {
                event.stopPropagation();
                setChangeWorkoutClipboard(row.workout_ids_array);
              }}
            >
              Copy
            </CopyButton>
            <CopyButton
              onClick={(event: any) => {
                event.stopPropagation();
                // eslint-disable-next-line no-unused-expressions
                changeWorkoutClipboard &&
                  changeWorkoutClipboard?.length > 0 &&
                  onRowChange({
                    ...row,
                    workout_ids_array: changeWorkoutClipboard.filter(
                      (workout) => workout.id !== row.workout_id,
                    ),
                  });
                // onClose();
              }}
            >
              Past
            </CopyButton>
          </div>
        );
      },
    },
    {
      key: 'alt_workouts_part',
      name: '대체부위',
      width: 80,
      editor: PartAutoCompleteComponent,
      editorOptions: {
        editOnClick: true,
      },
      formatter: AltWorkoutsPartComponent,
    },
    {
      key: 'challenge_condition',
      name: '1RM 기준 운동명',
      width: 200,
      editor: OneRmAutoCompleteComponent,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row }) {
        if (row.name === 'isRest') {
          return <EmptyCell />;
        }

        return <span>{row.challenge_condition?.name}</span>;
      },
    },
    {
      key: 'challenge_condition_part',
      name: '운동부위',
      width: 80,
      formatter({ row }) {
        if (row.name === 'isRest') {
          return <EmptyCell />;
        }

        return <span>{row.challenge_condition?.part}</span>;
      },
    },
    {
      key: 'weight_multiplier',
      name: '무게(1RM%)',
      width: 80,
      editor: TextEditor,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row }) {
        return <RestFormmater row={row} columnName='weight_multiplier' />;
      },
    },

    {
      key: 'weight_kg',
      name: '고정kg',
      width: 80,
      editor: TextEditor,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row }) {
        return <RestFormmater row={row} columnName='weight_kg' />;
      },
    },
    {
      key: 'weight_lbs',
      name: '고정lbs',
      width: 80,
      editor: TextEditor,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row }) {
        return <RestFormmater row={row} columnName='weight_lbs' />;
      },
    },
    {
      key: 'body_weight_multiplier',
      name: '체중계수',
      width: 80,
      editor: TextEditor,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row }) {
        return <RestFormmater row={row} columnName='body_weight_multiplier' />;
      },
    },
    {
      key: 'rir',
      name: 'RIR',
      width: 80,
      editor: TextEditor,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row }) {
        return <RirFormmater row={row} columnName='rir' />;
      },
    },
    {
      key: 'weight_placeholder',
      name: 'P/H',
      width: 80,
      editor: TextEditor,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row }) {
        return <RestFormmater row={row} columnName='weight_placeholder' />;
      },
    },
    {
      key: 'count',
      name: '횟수',
      width: 80,
      editor: TextEditor,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row }) {
        return <RestFormmater row={row} columnName='count' />;
      },
    },
    {
      key: 'count_placeholder',
      name: 'P/H',
      width: 80,
      editor: TextEditor,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row }) {
        return <RestFormmater row={row} columnName='count_placeholder' />;
      },
    },
    {
      key: 'set_count',
      name: '세트',
      width: 30,
      editor: TextEditor,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row }) {
        return <RestFormmater row={row} columnName='set_count' />;
      },
    },
    {
      key: 'rest_time_sec',
      name: '휴식(초)',
      width: 80,
      editor: TextEditor,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row }) {
        return <RestFormmater row={row} columnName='rest_time_sec' />;
      },
    },
    {
      key: 'tip',
      name: '팁',
      width: 200,
      editor: TextEditor,
      editorOptions: {
        editOnClick: true,
      },
      formatter({ row }) {
        return <RestFormmater row={row} columnName='tip' />;
      },
      // formatter: RoutineTipFormatter,
    },

    {
      key: 'order',
      name: '',
      formatter({ row }) {
        if (row.name === 'isRest') {
          return <EmptyCell />;
        }

        return (
          <RoutineOrderComponent
            row={row}
            routine={routine}
            nextOrderRoutine={nextOrderRoutine}
            prevOrderRoutine={prevOrderRoutine}
          />
        );
      },
    },
    {
      key: 'next',
      name: '',
      width: 40,
      formatter({ row }) {
        if (row.name === 'isRest') {
          return <EmptyCell />;
        }
        return (
          <IconButton
            aria-label='new'
            size='small'
            style={{ padding: 2, marginTop: '-4px' }}
            onClick={() => newNextRowRoutine(row)}
          >
            <Icon icon={plusOutlinedIcon} color='#a8a8a8' />
          </IconButton>
        );
      },
      groupFormatter({ row }) {
        return (
          <HeaderButtonWrapper>
            <RestButton
              onClick={() => onChangeRestDay(row.childRows[0].date)}
              style={{
                background:
                  row.childRows[0].name === 'isRest' ? '#4FA85E' : '#FF4842',
              }}
            >
              휴식
            </RestButton>
          </HeaderButtonWrapper>
        );
      },
    },
    {
      key: 'copy',
      name: '',
      width: 40,
      formatter({ row }) {
        if (row.name === 'isRest') {
          return <EmptyCell />;
        }
        return (
          <IconButton
            aria-label='new'
            size='small'
            style={{ padding: 2, marginTop: '-4px' }}
            onClick={() => copyNextRowRoutine(row)}
          >
            <Icon icon={copyOutlinedIcon} color='#a8a8a8' />
          </IconButton>
        );
      },
    },
    {
      key: 'delete',
      name: '',
      width: 40,
      formatter({ row }) {
        if (row.name === 'isRest') {
          return <EmptyCell />;
        }
        const isFirstRow = routine.filter(
          (v: IChallengeRoutines) => v.date === row.date,
        )[0];

        return (
          <IconButton
            aria-label='new'
            size='small'
            style={{ padding: 2, marginTop: '-4px' }}
            onClick={() => deleteRowRoutine(row)}
            disabled={isFirstRow.id === row.id}
          >
            <Icon
              icon={deleteOutlinedIcon}
              color={isFirstRow.id === row.id ? '#dedede' : '#ff6d6d'}
            />
          </IconButton>
        );
      },
      groupFormatter({ row }) {
        return (
          <HeaderButtonWrapper>
            <DuplicateButton
              onClick={() =>
                setDuplicateModal({
                  isOpen: true,
                  selectedDay: row.childRows[0].date,
                })
              }
            >
              복사
            </DuplicateButton>
          </HeaderButtonWrapper>
        );
      },
    },
  ];

  const handleKeyDown = () => {
    // console.log('handleKeyDown');
  };

  useEffect(() => {
    if (dataGridRef.current) {
      dataGridRef.current.element.addEventListener('keydown', handleKeyDown);
    }
  }, [dataGridRef]);

  useEffect(() => {
    if (expandedGroupIds.size === 0 && defaultExpendedGroup.length > 0) {
      setExpandedGroupIds(new Set<unknown>(defaultExpendedGroup));
    }
  }, [defaultExpendedGroup, expandedGroupIds]);

  const onRowChange = (row: any, data: any) => {
    const { indexes } = data;

    updateValue(row[indexes[0]], indexes[0]);
  };

  const saveDuplicateModal = (
    duplicate: { day: number; checked: boolean; selectedDay: boolean }[],
    selectedDay: number,
  ) => {
    makeDuplicateRoutines(duplicate, selectedDay, routine);
  };

  const rowHeight = (args: RowHeightArgs<IChallengeRoutines>) => {
    if (args.type === 'ROW') {
      if (args.row.workout_ids_array && args.row.workout_ids_array.length > 0) {
        return 34 * (args.row.workout_ids_array.length + 1);
      }

      // if (args.row.tip && args.row.tip.length > 0) {
      //   return 34 * (Math.floor(args.row.tip.length / 14) + 1);
      // }
    }

    return 34;
  };

  return (
    <div
      style={{
        height: '100%',
      }}
    >
      <ChallengeDuplicateModal
        selectedDay={duplicateModal.selectedDay}
        totalDate={totalDate}
        isOpen={duplicateModal.isOpen}
        toggleDuplicateModal={() =>
          setDuplicateModal({ selectedDay: 0, isOpen: false })
        }
        saveDuplicateModal={saveDuplicateModal}
      />
      <DataGrid
        style={{
          height: '100%',
        }}
        ref={dataGridRef}
        // onKeyDown={onKeyDown}
        rowRenderer={RowRenderer}
        onRowsChange={onRowChange}
        columns={columns}
        rows={routine}
        rowKeyGetter={rowKeyGetter}
        className='fill-grid'
        // selectedRows={selectedRows}
        // onSelectedRowsChange={setSelectedRows}
        rowHeight={rowHeight}
        groupBy={['date']}
        rowGrouper={rowGrouper}
        expandedGroupIds={expandedGroupIds}
        onExpandedGroupIdsChange={setExpandedGroupIds}
        defaultColumnOptions={{ resizable: true }}
      />
    </div>
  );
}
